import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { MovimientoTesoreria, MovimientoTesoreriaFiltro } from '../types/movimiento';
import { MovimientoTesoreriaService } from '../services/movimientoService';

interface MovimientoState {
  movimientos: MovimientoTesoreria[];
  loading: boolean;
  error: string | null;
  selectedMovimiento: MovimientoTesoreria | null;
  filtro: MovimientoTesoreriaFiltro;

  fetchMovimientos: () => Promise<void>;
  setFiltro: (filtro: Partial<MovimientoTesoreriaFiltro>) => void;
  anularMovimiento: (id: number) => Promise<void>;
  deleteMovimiento: (id: number) => Promise<void>;
  setSelectedMovimiento: (movimiento: MovimientoTesoreria | null) => void;
  clearError: () => void;
}

export const useMovimientoStore = create<MovimientoState>()(
  devtools(
    (set, get) => ({
      movimientos: [],
      loading: false,
      error: null,
      selectedMovimiento: null,
      filtro: {},

      fetchMovimientos: async () => {
        set({ loading: true, error: null }, false, 'movimientos/fetch/start');
        try {
          const movimientos = await MovimientoTesoreriaService.getAll(get().filtro);
          set({ movimientos, loading: false }, false, 'movimientos/fetch/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cargar movimientos';
          set({ loading: false, error: errorMessage }, false, 'movimientos/fetch/error');
        }
      },

      setFiltro: (filtro) => set((state) => ({ filtro: { ...state.filtro, ...filtro } }), false, 'movimientos/setFiltro'),

      anularMovimiento: async (id: number) => {
        set({ loading: true, error: null }, false, 'movimientos/anular/start');
        try {
          await MovimientoTesoreriaService.anular(id);
          set((state) => ({
            movimientos: state.movimientos.map(m =>
              m.idMovimientoTesoreria === id ? { ...m, estado: 'ANULADO' } : m
            ),
            loading: false,
          }), false, 'movimientos/anular/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al anular movimiento';
          set({ loading: false, error: errorMessage }, false, 'movimientos/anular/error');
          throw error;
        }
      },

      deleteMovimiento: async (id: number) => {
        set({ loading: true, error: null }, false, 'movimientos/delete/start');
        try {
          await MovimientoTesoreriaService.delete(id);
          set((state) => ({
            movimientos: state.movimientos.filter(m => m.idMovimientoTesoreria !== id),
            loading: false,
          }), false, 'movimientos/delete/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al eliminar movimiento';
          set({ loading: false, error: errorMessage }, false, 'movimientos/delete/error');
          throw error;
        }
      },

      setSelectedMovimiento: (movimiento) => set({ selectedMovimiento: movimiento }, false, 'movimientos/select'),
      clearError: () => set({ error: null }, false, 'movimientos/clearError'),
    }),
    { name: 'MovimientoStore' }
  )
);
